/**
 * API service layer for Vertex backend
 * All API calls go through this module
 */

import type {
  Vertex,
  Preview,
  Archive,
  PreviewsResponse,
  ShareResponse,
  CheckVertexExistsResult,
  UserKeyCheckResponse,
  CreateUserKeyRequest,
  CreateUserKeyResponse,
  UpdateUserKeyRequest,
  UserSessions,
  VertexSetElem,
  VertexSetWithVertices,
  CreateVertexSetRequest,
  UpdateVertexSetRequest,
  VertexSetUnion,
  SharedVertexSetInfo,
  SharedVertexSetResponse,
  PublicVertexSetMetadata,
  ArchiveTimesBatchResponse,
} from '../types';

declare global {
  interface Window {
    archiveShareInfo?: ArchiveTimesBatchResponse;
  }
}

// @ts-ignore - constants.js is vanilla JS
import { getApiEndpoint, vertexFetch } from '../../constants.js';

// ============================================================================
// API Client
// ============================================================================

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  params?: Record<string, string>;
  noAuth?: boolean;
  cache?: RequestCache;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Extract a user-facing error message from an ApiError's body.
 * The backend serialises `IsCoreError` as `{errors: [{code, message, ...}], services}`;
 * fall back to the HTTP status when no message is present.
 */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { errors?: { message?: string | null }[] } | undefined;
    const msg = body?.errors?.[0]?.message;
    if (msg) return msg;
    return err.message;
  }
  return err instanceof Error ? err.message : 'Unknown error';
}

export class AuthError extends Error {
  constructor() {
    super('Authentication required');
    this.name = 'AuthError';
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, noAuth = false, cache } = options;

  const endpoint = await getApiEndpoint();

  // Build URL with query params
  let url = `${endpoint}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      searchParams.append(key, value);
    }
    url += `?${searchParams.toString()}`;
  }

  // Build headers - no Bearer token, rely on cookies
  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await vertexFetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    cache,
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthError();
    }
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, `API error: ${response.status}`, body);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// Convenience methods
const api = {
  get: <T>(path: string, params?: Record<string, string>, options?: Omit<RequestOptions, 'method' | 'params'>) =>
    request<T>(path, { ...options, method: 'GET', params }),

  post: <T>(path: string, body?: unknown, params?: Record<string, string>) =>
    request<T>(path, { method: 'POST', body, params }),

  put: <T>(path: string, body?: unknown, params?: Record<string, string>) =>
    request<T>(path, { method: 'PUT', body, params }),

  delete: <T>(path: string, body?: unknown, params?: Record<string, string>) =>
    request<T>(path, { method: 'DELETE', body, params }),
};

// ============================================================================
// User / Authentication
// ============================================================================

export async function createUserKey(userAlias?: string | null): Promise<CreateUserKeyResponse> {
  const body: CreateUserKeyRequest = {};
  if (userAlias !== undefined) {
    body.userAlias = userAlias;
  }
  // Server sets auth cookie on key creation
  return request<CreateUserKeyResponse>('/user/key', { method: 'POST', body, noAuth: true });
}

/**
 * Rotate the user's key. Generates a new key and invalidates the old one.
 * Requires Proof of Possession: the current key must be provided in the
 * Authorization header, and the cookie must also be valid.
 * Server sets a new auth cookie for the new key.
 * @param currentKey - The user's current key (for PoP verification)
 * @returns The new key (to display to user for saving) and user alias
 */
export async function rotateUserKey(currentKey: string): Promise<CreateUserKeyResponse> {
  const endpoint = await getApiEndpoint();
  const response = await vertexFetch(`${endpoint}/user/key/rotate`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${currentKey}`,
    },
  });

  if (!response.ok) {
    throw new ApiError(response.status, `Key rotation failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Logout - clears the auth cookie on the server.
 */
export async function logout(): Promise<void> {
  const endpoint = await getApiEndpoint();
  const response = await vertexFetch(`${endpoint}/user/logout`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Logout failed: ${response.status}`);
  }
  // Response may be empty, don't try to parse JSON
}

/**
 * Authenticate with a Bearer token via the /user/login endpoint.
 * This endpoint ignores any existing (potentially stale) cookies and
 * authenticates strictly via the Bearer token. On success, it sets a
 * fresh auth cookie for subsequent requests.
 * @returns User info if valid, null if invalid
 */
export async function authenticateWithKey(key: string): Promise<UserKeyCheckResponse | null> {
  try {
    const endpoint = await getApiEndpoint();
    const response = await vertexFetch(`${endpoint}/user/login`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${key}` },
      cache: 'no-store',
    });
    if (!response.ok) {
      return null;
    }
    return response.json();
  } catch (_) {
    return null;
  }
}

/**
 * Check if currently authenticated (via cookie).
 * @returns User info if authenticated, null otherwise
 */
export async function checkAuthentication(): Promise<UserKeyCheckResponse | null> {
  try {
    return await api.get<UserKeyCheckResponse>('/user/key/check', undefined, { cache: 'no-store' });
  } catch (_) {
    return null;
  }
}

export async function checkUserKey(): Promise<UserKeyCheckResponse | null> {
  // Now uses cookie-based auth, no need to check for stored key
  return checkAuthentication();
}

export interface ValidateUserKeyResult {
  valid: boolean;
  userAlias: string | null;
}

export async function validateUserKey(key: string): Promise<ValidateUserKeyResult> {
  // This also sets the auth cookie on success
  const result = await authenticateWithKey(key);
  if (result) {
    return { valid: true, userAlias: result.userAlias };
  }
  return { valid: false, userAlias: null };
}

export async function updateUserKey(userAlias: string | null): Promise<void> {
  const body: UpdateUserKeyRequest = { userAlias };
  await api.put<void>('/user/key', body);
}

export async function checkAliasAvailability(alias: string): Promise<boolean> {
  const response = await api.get<{ available: boolean }>(
    '/user/key/alias/availability',
    { alias },
    { noAuth: true }
  );
  return response.available === true;
}

// Dynamic import to avoid circular dependency
let storageModule: { STORAGE_KEYS: { USER_ALIAS: string }, storageSet: (k: string, v: any) => Promise<void>, storageRemove: (k: string) => Promise<void> } | null = null;
async function getStorageModule() {
  if (!storageModule) {
    const constants = await import('../../constants.js');
    storageModule = {
      STORAGE_KEYS: constants.STORAGE_KEYS,
      storageSet: constants.storageSet,
      storageRemove: constants.storageRemove,
    };
  }
  return storageModule;
}

/**
 * Sync user data from the server (alias, etc.)
 * Call this on app load to ensure local storage is up to date.
 */
export async function syncUserData(): Promise<void> {
  try {
    const result = await checkUserKey();
    if (!result) return;

    const { STORAGE_KEYS, storageSet, storageRemove } = await getStorageModule();
    if (result.userAlias !== null) {
      await storageSet(STORAGE_KEYS.USER_ALIAS, result.userAlias);
    } else {
      await storageRemove(STORAGE_KEYS.USER_ALIAS);
    }
  } catch (err) {
    console.error('[Vertex] Error syncing user data:', err);
  }
}

// ============================================================================
// Billing
// ============================================================================

export interface BillingSubscription {
  isActive: boolean;
  rawJson?: {
    billing_cycle?: {
      interval: string;
      frequency: number;
    };
    items?: Array<{
      product?: { name?: string };
    }>;
  };
}

export interface BillingMeta {
  billingEntityId: string;
  subscription: BillingSubscription | 'whitelisted' | null;
}

export function billingPlanName(subscription: BillingMeta['subscription']): string {
  if (subscription === null) return 'Free';
  if (subscription === 'whitelisted') return 'Pro';
  const product = subscription.rawJson?.items?.[0]?.product?.name;
  const interval = subscription.rawJson?.billing_cycle?.interval;
  const planName = product || 'Pro';
  if (interval === 'month') return `${planName} (Monthly)`;
  if (interval === 'year') return `${planName} (Yearly)`;
  return planName;
}

export function hasProAccess(subscription: BillingMeta['subscription']): boolean {
  if (subscription === 'whitelisted') return true;
  return subscription !== null && subscription.isActive;
}

export async function fetchBillingMeta(): Promise<BillingMeta> {
  return api.get<BillingMeta>('/user/billing/meta');
}

export async function createBillingPortalSession(): Promise<{ uri: string }> {
  return api.post<{ uri: string }>('/billing/paddle/client-session');
}

// ============================================================================
// Archive Lifecycle & Notifications
// ============================================================================

export type NotificationTopic = 'ColdStorage' | 'Expiry';

export interface ColdStorageResponse {
  coldStorage: Record<string, string[]>;
  coldStorageLimit: number;
}

export interface ExpiringResponse {
  expiring: Record<string, string[]>;
  expirationLimit: number;
}

export interface ArchiveNotification {
  vertex: string;
  archiveTime: string;
  topic: NotificationTopic;
  limitSeconds: number;
}

export interface NotificationAck {
  topic: NotificationTopic;
  vertex: string;
  archiveTime: string;
}

export interface NotificationAckResponse {
  topic: NotificationTopic;
  vertex: { rendered: string };
  archiveTime: string;
}

export async function fetchColdStorageSoon(within: string): Promise<ColdStorageResponse> {
  return api.get<ColdStorageResponse>('/user/vertex/archive/lifecycle/cold-storage', { within });
}

export async function fetchExpiringSoon(within: string): Promise<ExpiringResponse> {
  return api.get<ExpiringResponse>('/user/vertex/archive/lifecycle/expiring', { within });
}

export async function acknowledgeNotification(ack: NotificationAck): Promise<void> {
  return api.post<void>('/user/vertex/archive/notif/ack', ack);
}

export async function fetchAcknowledgedNotifications(): Promise<NotificationAckResponse[]> {
  return api.get<NotificationAckResponse[]>('/user/vertex/archive/notif/acks');
}

// ============================================================================
// Sessions
// ============================================================================

export async function fetchSessions(): Promise<UserSessions> {
  return api.get<UserSessions>('/user/sessions');
}

export async function deleteSession(sessionId: string): Promise<void> {
  await api.delete<void>('/user/session', undefined, { sessionId });
}

// ============================================================================
// Vertices
// ============================================================================

export async function fetchBatchVertices(urls: string[]): Promise<Record<string, { note: string }>> {
  return api.post<Record<string, { note: string }>>('/user/vertices/batch', urls);
}

export async function fetchAllVertices(): Promise<Vertex[]> {
  return api.get<Vertex[]>('/user/vertices');
}

export async function fetchVertex(url: string): Promise<Vertex> {
  return api.get<Vertex>('/user/vertex', { vertex: url });
}

export async function checkVertexExists(url: string): Promise<CheckVertexExistsResult> {
  try {
    const vertex = await api.get<Vertex>('/user/vertex', { vertex: url });
    return { status: 200, vertex };
  } catch (error) {
    if (error instanceof ApiError) {
      return { status: error.status, vertex: null };
    }
    throw error;
  }
}

export async function createVertex(url: string, note: string | null): Promise<Vertex> {
  return api.post<Vertex>('/user/vertex', {
    vertex: url,
    note,
    archiveShareMode: 'OnlySharedArchives',
  });
}

export async function updateVertex(url: string, note: string | null): Promise<Vertex> {
  return api.put<Vertex>('/user/vertex', { note }, { vertex: url });
}

export async function updateArchiveShareMode(
  url: string,
  archiveShareMode: 'OnlySharedArchives' | 'AllArchives'
): Promise<Vertex> {
  return api.put<Vertex>('/user/vertex', { archiveShareMode }, { vertex: url });
}

export async function deleteVertices(urls: string[]): Promise<number> {
  return api.delete<number>('/user/vertices/batch', urls);
}

// ============================================================================
// Previews
// ============================================================================

export async function fetchPreviewsBatch(urls: string[]): Promise<Record<string, Preview>> {
  if (urls.length === 0) return {};

  try {
    const data = await api.post<PreviewsResponse>('/user/vertex/previews/batch', urls);

    // Unwrap { url: { vertex, preview } } -> { url: preview }
    const result: Record<string, Preview> = {};
    for (const [url, entry] of Object.entries(data)) {
      if (entry.preview) {
        result[url] = entry.preview;
      }
    }
    return result;
  } catch (error) {
    console.error('Failed to fetch previews:', error);
    return {};
  }
}

/**
 * Upsert a preview (POST - creates if doesn't exist, overwrites all fields)
 * Use when vertex is new or when you have complete preview data.
 */
export async function upsertPreview(url: string, preview: Preview): Promise<void> {
  await api.post<void>('/user/vertex/preview', preview, { vertex: url });
}

/**
 * Update an existing preview (PUT - conservative, only updates provided fields)
 * Use when vertex exists and preview might exist. Fails if preview doesn't exist.
 */
export async function updatePreview(url: string, preview: Partial<Preview>): Promise<void> {
  await api.put<void>('/user/vertex/preview', preview, { vertex: url });
}

// ============================================================================
// Archives
// ============================================================================

export async function fetchArchiveTimesBatch(urls: string[]): Promise<Record<string, string[]>> {
  if (urls.length === 0) return {};

  try {
    const data = await api.post<ArchiveTimesBatchResponse>('/user/vertex/archives/times/batch', urls);

    // Store full data in window for shareId lookups (browser context only)
    if (typeof window !== 'undefined') {
      window.archiveShareInfo = data;
    }

    // Convert { url: { timestamp: {...} } } -> { url: [timestamps] }
    const result: Record<string, string[]> = {};
    for (const [url, timestampMap] of Object.entries(data)) {
      result[url] = Object.keys(timestampMap);
    }
    return result;
  } catch (error) {
    console.error('Failed to fetch archive times:', error);
    return {};
  }
}

/**
 * Ask the backend to capture the URL server-side (headless Chromium via CDP).
 * Long-running (5-30s). Resolves once the archive is stored; caller should
 * refresh archive times after.
 */
export function backendArchiveVertex(url: string, note: string | null = null): Promise<void> {
  return api.post<void>('/user/vertex/archive/backend', { note }, { vertex: url }).then(() => undefined);
}

export type CaptureQuotaReport =
  | { status: 'unlimited' }
  | { status: 'remaining'; used: number; limit: number }
  | { status: 'reached'; used: number; limit: number };

export interface ArchiveSourcesToday {
  sources: Record<string, number>;
  captureQuota: CaptureQuotaReport;
}

export function fetchArchiveSourcesToday(): Promise<ArchiveSourcesToday> {
  return api.get<ArchiveSourcesToday>('/user/vertex/archives/sources');
}

export async function fetchArchive(url: string, timestamp: string): Promise<Archive> {
  const archives = await api.get<Archive[]>('/user/vertex/archives', { vertex: url, timestamp });

  if (!archives || archives.length === 0) {
    throw new Error('Archive not found');
  }
  return archives[0];
}

export async function updateArchiveNote(
  url: string,
  timestamp: string,
  note: string | null
): Promise<void> {
  await api.put<void>('/user/vertex/archive', { note }, { vertex: url, timestamp });
}

export async function shareArchive(url: string, timestamp: string): Promise<string> {
  const data = await api.post<ShareResponse>('/user/vertex/archive/share', undefined, { vertex: url, timestamp });
  return data.shareId;
}

export async function deleteArchive(url: string, timestamp: string): Promise<void> {
  await api.delete<void>('/user/vertex/archive', undefined, { vertex: url, timestamp });
}

// ============================================================================
// Vertex Sets
// ============================================================================

export async function createVertexSet(req: CreateVertexSetRequest): Promise<VertexSetWithVertices> {
  return api.post<VertexSetWithVertices>('/user/vertex/set', req);
}

export async function fetchAllVertexSets(
  containingVertices?: string[],
): Promise<VertexSetWithVertices[]> {
  const params =
    containingVertices && containingVertices.length > 0
      ? { containing_vertices: containingVertices.join(',') }
      : undefined;
  return api.get<VertexSetWithVertices[]>('/user/vertex/sets', params);
}

export async function fetchVertexSet(setId: string): Promise<VertexSetWithVertices> {
  return api.get<VertexSetWithVertices>('/user/vertex/set', { vertexSetId: setId });
}

export async function updateVertexSet(setId: string, req: UpdateVertexSetRequest): Promise<VertexSetWithVertices> {
  return api.put<VertexSetWithVertices>('/user/vertex/set', req, { vertexSetId: setId });
}

export async function deleteVertexSet(setId: string): Promise<void> {
  await api.delete<void>('/user/vertex/set', undefined, { vertexSetId: setId });
}

export async function addVertexToSet(setId: string, vertexUrl: string): Promise<void> {
  await api.post<void>('/user/vertex/set/elem', { vertex: vertexUrl }, { vertexSetId: setId });
}

export async function removeVertexFromSet(setId: string, vertexUrl: string): Promise<void> {
  await api.delete<void>('/user/vertex/set/elem', undefined, { vertexSetId: setId, vertex: vertexUrl });
}

export async function fetchVertexSetElems(setId: string): Promise<VertexSetElem[]> {
  return api.get<VertexSetElem[]>('/user/vertex/set/elems', { vertexSetId: setId });
}

export interface MoveRequest {
  move: string;
  to: { before: string } | { after: string };
}

export async function reorderVertexInSet(setId: string, move: MoveRequest): Promise<VertexSetElem[]> {
  return api.put<VertexSetElem[]>('/user/vertex/set/elems/reorder', move, { vertexSetId: setId });
}

export async function shareVertexSet(setId: string): Promise<string> {
  const data = await api.post<ShareResponse>('/user/vertex/set/share', undefined, { vertexSetId: setId });
  return data.shareId;
}

// ============================================================================
// Vertex Set Unions
// ============================================================================

export async function createVertexSetUnion(
  vertexSetId: string,
  targetShareId: string
): Promise<void> {
  await api.post<void>('/user/vertex/set/union', undefined, { vertexSetId, targetShareId });
}

export async function fetchVertexSetUnions(
  vertexSetId?: string,
  targetShareId?: string
): Promise<VertexSetUnion[]> {
  const params: Record<string, string> = {};
  if (vertexSetId) params.vertexSetId = vertexSetId;
  if (targetShareId) params.targetShareId = targetShareId;
  return api.get<VertexSetUnion[]>('/user/vertex/set/unions', Object.keys(params).length > 0 ? params : undefined);
}

export async function deleteVertexSetUnion(
  vertexSetId: string,
  targetShareId: string
): Promise<void> {
  await api.delete<void>('/user/vertex/set/union', undefined, { vertexSetId, targetShareId });
}

export async function fetchSharedVertexSetInfo(shareId: string): Promise<SharedVertexSetInfo> {
  const endpoint = await getApiEndpoint();
  const response = await vertexFetch(`${endpoint}/vertex/set/${shareId}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new ApiError(response.status, `Failed to fetch shared set: ${response.status}`);
  }

  const data: SharedVertexSetResponse = await response.json();

  return {
    name: data.vertexSet.vertexSet.name,
    description: data.vertexSet.vertexSet.description,
    ownerAlias: data.userAlias,
    vertexCards: data.vertexCards,
  };
}

// ============================================================================
// User Alias Search
// ============================================================================

export async function searchUserAliases(query: string, limit = 10): Promise<string[]> {
  const endpoint = await getApiEndpoint();
  const params = new URLSearchParams({ userAlias: query, limit: String(limit) });
  const response = await vertexFetch(`${endpoint}/user/key/alias/search?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new ApiError(response.status, 'Failed to search aliases');
  }
  return response.json();
}

export async function fetchPublicVertexSetsByAlias(alias: string): Promise<PublicVertexSetMetadata[]> {
  const endpoint = await getApiEndpoint();
  const params = new URLSearchParams({ userAlias: alias });
  const response = await vertexFetch(`${endpoint}/vertex/sets?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new ApiError(response.status, 'Failed to fetch sets');
  }
  return response.json();
}
